export const INK_DEVICE_PROFILE_SCHEMA_VERSION = 2;
export const INK_ENGINE_VERSION = "2.9";
export const DIAGNOSTIC_CONSENT_VERSION = "2026-08-30";
export const INK_DEVICE_PROFILE_UPDATED_EVENT = "inksolver:ink-device-profile-updated";
export const DEFAULT_PALM_STYLUS_SEPARATION_PX = 120;
export const MIN_PALM_STYLUS_SEPARATION_PX = 60;
export const MAX_PALM_STYLUS_SEPARATION_PX = 260;

const INSTALLATION_ID_KEY = "inksolver:ink-device-installation-id";
export const INK_DEVICE_PROFILE_STORAGE_KEY = "inksolver:ink-device-profile:v2";

export const NEUTRAL_INK_SETTINGS = Object.freeze({
  inputMode: "auto",
  writingTool: "auto-detect",
  nibOffsetX: 0,
  nibOffsetY: 0,
  minPointDistance: 0.75,
  coalescedLimit: 64,
  interpolationSegmentLength: 0,
  lineWidth: 2.5,
  smoothingStrength: 0,
  velocityResponse: 0.7,
  predictionMs: 0,
  palmGuardEnabled: false,
  palmGuardHeightRatio: 0.28,
  palmGuardAutoAdvance: false,
  palmCalibrationReady: false,
  palmStylusSeparationPx: DEFAULT_PALM_STYLUS_SEPARATION_PX,
  palmTouchContactPx: 0,
  palmHardRejectContactPx: 0,
  palmBroadContactRatio: 1.35,
  palmSecondaryContactRatio: 1,
  rejectWidePrimaryTouch: false
});

// Device Check deliberately starts from direct, platform-neutral ink. Practice
// needs a different fallback: these are the proven presets used before device
// calibration existed, so an uncalibrated user does not inherit test settings.
const STABLE_RUNTIME_OVERRIDES = Object.freeze({
  desktop: Object.freeze({
    minPointDistance: 1.5,
    coalescedLimit: 24,
    interpolationSegmentLength: 0,
    palmTouchContactPx: 28,
    palmHardRejectContactPx: 92,
    palmBroadContactRatio: 1.15,
    palmSecondaryContactRatio: 0.72,
    rejectWidePrimaryTouch: true
  }),
  "ios-touch": Object.freeze({
    minPointDistance: 0.9,
    coalescedLimit: 24,
    interpolationSegmentLength: 4.5,
    palmTouchContactPx: 26,
    palmHardRejectContactPx: 96,
    palmBroadContactRatio: 1.15,
    palmSecondaryContactRatio: 0.72,
    rejectWidePrimaryTouch: false
  }),
  "android-touch": Object.freeze({
    minPointDistance: 0.1,
    coalescedLimit: 160,
    interpolationSegmentLength: 1.4,
    palmTouchContactPx: 11.9,
    palmHardRejectContactPx: 17.3,
    palmBroadContactRatio: 1.15,
    palmSecondaryContactRatio: 0.72,
    rejectWidePrimaryTouch: true
  }),
  "windows-touch": Object.freeze({
    minPointDistance: 0.8,
    coalescedLimit: 120,
    interpolationSegmentLength: 2,
    palmTouchContactPx: 80,
    palmHardRejectContactPx: 118,
    palmBroadContactRatio: 1.15,
    palmSecondaryContactRatio: 0.72,
    rejectWidePrimaryTouch: false
  }),
  "touch-device": Object.freeze({
    minPointDistance: 0.75,
    coalescedLimit: 64,
    interpolationSegmentLength: 0
  })
});

const INPUT_MODES = new Set(["auto", "pen-only", "touch-only"]);
const WRITING_TOOLS = new Set(["auto-detect", "digital-pen", "active-stylus", "capacitive-stylus", "finger", "mouse"]);

export const INK_WRITING_TOOL_OPTIONS = Object.freeze([
  Object.freeze({
    value: "digital-pen",
    label: "Digital Pen",
    description: "Apple Pencil, S Pen, Surface Pen, or a pen paired with the device"
  }),
  Object.freeze({
    value: "capacitive-stylus",
    label: "Capacitive Stylus",
    description: "A passive disk, rubber, or mesh-tip stylus"
  }),
  Object.freeze({
    value: "active-stylus",
    label: "Active Stylus",
    description: "A battery or rechargeable metal/plastic-tip stylus"
  }),
  Object.freeze({
    value: "finger",
    label: "Finger",
    description: "Write directly with your fingertip"
  })
]);

export function getInkWritingToolLabel(value) {
  return INK_WRITING_TOOL_OPTIONS.find((option) => option.value === value)?.label
    || (value === "mouse" ? "Mouse or trackpad" : "Not selected");
}

function clampNumber(value, fallback, min, max) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

function sanitizePalmStylusSeparation(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_PALM_STYLUS_SEPARATION_PX;
  return Math.max(
    MIN_PALM_STYLUS_SEPARATION_PX,
    Math.min(MAX_PALM_STYLUS_SEPARATION_PX, parsed)
  );
}

export function sanitizeInkSettings(input = {}) {
  const inputMode = String(input.inputMode || "").trim().toLowerCase();
  const writingTool = String(input.writingTool || "").trim().toLowerCase();
  const palmTouchContactPx = clampNumber(
    input.palmTouchContactPx,
    NEUTRAL_INK_SETTINGS.palmTouchContactPx,
    0,
    180
  );
  const hardFallback = palmTouchContactPx > 0
    ? Math.max(palmTouchContactPx + 4, palmTouchContactPx * 2.4)
    : 0;

  return {
    inputMode: INPUT_MODES.has(inputMode) ? inputMode : NEUTRAL_INK_SETTINGS.inputMode,
    writingTool: WRITING_TOOLS.has(writingTool) ? writingTool : NEUTRAL_INK_SETTINGS.writingTool,
    nibOffsetX: clampNumber(input.nibOffsetX, NEUTRAL_INK_SETTINGS.nibOffsetX, -32, 32),
    nibOffsetY: clampNumber(input.nibOffsetY, NEUTRAL_INK_SETTINGS.nibOffsetY, -32, 32),
    minPointDistance: clampNumber(
      input.minPointDistance,
      NEUTRAL_INK_SETTINGS.minPointDistance,
      0.1,
      4
    ),
    coalescedLimit: Math.round(clampNumber(
      input.coalescedLimit,
      NEUTRAL_INK_SETTINGS.coalescedLimit,
      8,
      192
    )),
    interpolationSegmentLength: clampNumber(
      input.interpolationSegmentLength,
      NEUTRAL_INK_SETTINGS.interpolationSegmentLength,
      0,
      8
    ),
    lineWidth: clampNumber(input.lineWidth, NEUTRAL_INK_SETTINGS.lineWidth, 1.25, 5),
    smoothingStrength: clampNumber(
      input.smoothingStrength,
      NEUTRAL_INK_SETTINGS.smoothingStrength,
      0,
      0.9
    ),
    velocityResponse: clampNumber(
      input.velocityResponse,
      NEUTRAL_INK_SETTINGS.velocityResponse,
      0,
      1
    ),
    predictionMs: clampNumber(input.predictionMs, NEUTRAL_INK_SETTINGS.predictionMs, 0, 24),
    palmGuardEnabled: Boolean(input.palmGuardEnabled),
    palmGuardHeightRatio: clampNumber(
      input.palmGuardHeightRatio,
      NEUTRAL_INK_SETTINGS.palmGuardHeightRatio,
      0.16,
      0.42
    ),
    palmGuardAutoAdvance: input.palmGuardAutoAdvance === true,
    palmCalibrationReady: input.palmCalibrationReady === true,
    palmStylusSeparationPx: sanitizePalmStylusSeparation(input.palmStylusSeparationPx),
    palmTouchContactPx,
    palmHardRejectContactPx: clampNumber(
      input.palmHardRejectContactPx,
      hardFallback,
      palmTouchContactPx > 0 ? palmTouchContactPx : 0,
      240
    ),
    palmBroadContactRatio: clampNumber(
      input.palmBroadContactRatio,
      NEUTRAL_INK_SETTINGS.palmBroadContactRatio,
      1,
      2.5
    ),
    palmSecondaryContactRatio: clampNumber(
      input.palmSecondaryContactRatio,
      NEUTRAL_INK_SETTINGS.palmSecondaryContactRatio,
      0.5,
      2
    ),
    rejectWidePrimaryTouch: Boolean(input.rejectWidePrimaryTouch)
  };
}

function migrateInkSettings(input, sourceEngineVersion) {
  const settings = sanitizeInkSettings(input);
  if (sourceEngineVersion && sourceEngineVersion !== INK_ENGINE_VERSION) {
    return {
      ...settings,
      inputMode: "auto",
      coalescedLimit: Math.max(64, settings.coalescedLimit),
      interpolationSegmentLength: 0,
      smoothingStrength: Math.min(0.4, settings.smoothingStrength),
      predictionMs: 0,
      palmGuardEnabled: false,
      palmGuardAutoAdvance: false
    };
  }
  return settings.inputMode === "pen-only" ? { ...settings, inputMode: "auto" } : settings;
}

function makeInstallationId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const randomPart = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
  return `ink_${Date.now().toString(36)}_${randomPart.slice(0, 36)}`;
}

export function getInkDeviceInstallationId() {
  if (typeof window === "undefined") return "server-rendered-device";
  try {
    const existing = window.localStorage.getItem(INSTALLATION_ID_KEY);
    if (existing) return existing;
    const created = makeInstallationId();
    window.localStorage.setItem(INSTALLATION_ID_KEY, created);
    return created;
  } catch (err) {
    return makeInstallationId();
  }
}

export function readLocalInkDeviceProfile() {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(INK_DEVICE_PROFILE_STORAGE_KEY) || "null");
    if (!parsed || typeof parsed !== "object") return null;
    if (Number(parsed.schemaVersion) !== INK_DEVICE_PROFILE_SCHEMA_VERSION) return null;
    return {
      ...parsed,
      installationId: parsed.installationId || getInkDeviceInstallationId(),
      settings: migrateInkSettings(parsed.settings, parsed.engineVersion)
    };
  } catch (err) {
    return null;
  }
}

export function saveLocalInkDeviceProfile(input = {}, options = {}) {
  const now = new Date().toISOString();
  const profile = {
    schemaVersion: INK_DEVICE_PROFILE_SCHEMA_VERSION,
    engineVersion: INK_ENGINE_VERSION,
    installationId: input.installationId || getInkDeviceInstallationId(),
    label: String(input.label || "This device").trim().slice(0, 80) || "This device",
    environment: input.environment && typeof input.environment === "object" ? input.environment : {},
    settings: migrateInkSettings(input.settings, input.engineVersion),
    diagnosticSummary: input.diagnosticSummary && typeof input.diagnosticSummary === "object"
      ? input.diagnosticSummary
      : {},
    consent: input.consent || null,
    calibratedAt: input.calibratedAt || now,
    updatedAt: input.updatedAt || now,
    source: input.source || "local"
  };

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(INK_DEVICE_PROFILE_STORAGE_KEY, JSON.stringify(profile));
    } catch (err) {
      // Private browsing can deny storage; the in-memory caller still receives the profile.
    }
    if (options.broadcast !== false) {
      window.dispatchEvent(new CustomEvent(INK_DEVICE_PROFILE_UPDATED_EVENT, { detail: profile }));
    }
  }
  return profile;
}

export function clearLocalInkDeviceProfile() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(INK_DEVICE_PROFILE_STORAGE_KEY);
  } catch (err) {
    // No action is needed when browser storage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(INK_DEVICE_PROFILE_UPDATED_EVENT, { detail: null }));
}

export function hasCalibratedInkProfile(profile) {
  return Boolean(profile?.calibratedAt && profile?.settings && typeof profile.settings === "object");
}

export function getStableInkRuntimeSettings(presentationProfile = detectSlatePresentationProfile()) {
  const profileName = String(presentationProfile || "desktop");
  const overrides = STABLE_RUNTIME_OVERRIDES[profileName] || STABLE_RUNTIME_OVERRIDES.desktop;
  return sanitizeInkSettings({ ...NEUTRAL_INK_SETTINGS, ...overrides });
}

export function resolveInkRuntimeSettings(
  profile,
  presentationProfile = detectSlatePresentationProfile()
) {
  if (!hasCalibratedInkProfile(profile)) {
    return getStableInkRuntimeSettings(presentationProfile);
  }
  return sanitizeInkSettings(profile.settings);
}

export function detectSlatePresentationProfile() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return "desktop";
  const touch = Number(navigator.maxTouchPoints || 0) > 0
    || (typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches);
  if (!touch) return "desktop";
  const ua = String(navigator.userAgent || "");
  const platform = String(navigator.userAgentData?.platform || navigator.platform || "");
  if (/android/i.test(ua)) return "android-touch";
  if (/iphone|ipad|ipod/i.test(ua) || (platform === "MacIntel" && Number(navigator.maxTouchPoints || 0) > 1)) {
    return "ios-touch";
  }
  if (/windows|win/i.test(`${ua} ${platform}`)) return "windows-touch";
  return "touch-device";
}

function getBrowserDescription() {
  if (typeof navigator === "undefined") return "Unknown browser";
  const brands = navigator.userAgentData?.brands;
  if (Array.isArray(brands) && brands.length) {
    return brands.map((brand) => `${brand.brand} ${brand.version}`).join(", ").slice(0, 120);
  }
  const ua = String(navigator.userAgent || "");
  const match = ua.match(/(Edg|Chrome|CriOS|Firefox|FxiOS|Safari)\/?([\d.]+)?/i);
  return match ? `${match[1]} ${match[2] || ""}`.trim() : "Browser detected";
}

export function collectInkEnvironmentSnapshot() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return {};
  const coarse = typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
  const hover = typeof window.matchMedia === "function" && window.matchMedia("(hover: hover)").matches;
  return {
    platform: String(navigator.userAgentData?.platform || navigator.platform || "Unknown").slice(0, 80),
    browser: getBrowserDescription(),
    viewport: {
      width: Math.round(window.innerWidth || 0),
      height: Math.round(window.innerHeight || 0),
      devicePixelRatio: Number(window.devicePixelRatio || 1)
    },
    screen: {
      width: Math.round(window.screen?.width || 0),
      height: Math.round(window.screen?.height || 0)
    },
    maxTouchPoints: Number(navigator.maxTouchPoints || 0),
    pointer: coarse ? "coarse" : "fine",
    hover,
    supportsPointerEvents: typeof window.PointerEvent === "function",
    supportsCoalescedEvents: Boolean(window.PointerEvent?.prototype?.getCoalescedEvents),
    supportsRawPointerEvents: "onpointerrawupdate" in window,
    supportsPredictedEvents: Boolean(window.PointerEvent?.prototype?.getPredictedEvents)
  };
}

export function getPointerType(event) {
  return String(event?.pointerType || "mouse").trim().toLowerCase() || "mouse";
}

function isSamePointerSample(left, right) {
  if (!left || !right) return false;
  return Number(left.pointerId) === Number(right.pointerId)
    && Number(left.timeStamp) === Number(right.timeStamp)
    && Number(left.clientX) === Number(right.clientX)
    && Number(left.clientY) === Number(right.clientY);
}

export function getCoalescedPointerSamples(event, limit = 64) {
  const nativeEvent = event?.nativeEvent || event;
  if (!nativeEvent) return [];

  let samples = [];
  try {
    if (typeof nativeEvent.getCoalescedEvents === "function") {
      samples = Array.from(nativeEvent.getCoalescedEvents() || []);
    }
  } catch (err) {
    // Some Android WebViews expose the method but throw while a touch is changing ownership.
  }

  const currentIsUsable = Number.isFinite(Number(nativeEvent.clientX))
    && Number.isFinite(Number(nativeEvent.clientY));
  const lastSample = samples[samples.length - 1];
  if (currentIsUsable && !isSamePointerSample(lastSample, nativeEvent)) {
    samples.push(nativeEvent);
  }

  const safeLimit = Math.max(1, Math.round(Number(limit) || 64));
  return samples.filter(Boolean).slice(-safeLimit);
}

export function isPointerAllowed(pointerType, settings) {
  const mode = sanitizeInkSettings(settings).inputMode;
  const type = String(pointerType || "mouse").toLowerCase();
  if (mode === "pen-only") return type === "pen";
  if (mode === "touch-only") return type === "touch";
  return true;
}

export function getTouchContactMajor(event) {
  if (getPointerType(event) !== "touch") return 0;
  return Math.max(Number(event?.width || 0), Number(event?.height || 0), 0);
}

export function shouldRejectCalibratedTouch(event, settings, context = {}) {
  if (getPointerType(event) !== "touch") return false;
  const resolved = sanitizeInkSettings(settings);
  if (resolved.palmGuardEnabled) return false;
  const contact = getTouchContactMajor(event);
  const threshold = Number(resolved.palmTouchContactPx || 0);
  if (!(threshold > 0) || !(contact > 0)) return false;
  if (contact >= Number(resolved.palmHardRejectContactPx || threshold * 2.4)) return true;
  if (resolved.rejectWidePrimaryTouch && contact >= threshold * resolved.palmBroadContactRatio) return true;
  if (context.activePointer && event?.isPrimary === false && contact >= threshold * resolved.palmSecondaryContactRatio) {
    return true;
  }
  return Number(context.touchCount || 0) > 1 && contact >= threshold;
}

export function percentile(values, percentileValue) {
  const sorted = (Array.isArray(values) ? values : [])
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const index = Math.max(0, Math.min(sorted.length - 1, Math.ceil((percentileValue / 100) * sorted.length) - 1));
  return sorted[index];
}

export function createInkPointProcessor(settings) {
  const resolved = sanitizeInkSettings(settings);
  let lastRaw = null;
  let lastOutput = null;
  let lastTimestamp = null;
  let velocity = { x: 0, y: 0 };

  return {
    process(x, y, timestamp, options = {}) {
      const raw = { x: Number(x), y: Number(y) };
      const time = Number.isFinite(Number(timestamp)) ? Number(timestamp) : performance.now();
      if (!Number.isFinite(raw.x) || !Number.isFinite(raw.y)) return null;
      if (!lastRaw || !lastOutput || lastTimestamp === null) {
        lastRaw = raw;
        lastOutput = raw;
        lastTimestamp = time;
        return { x: raw.x, y: raw.y, trackingLagPx: 0 };
      }

      const deltaSeconds = Math.max(1 / 240, Math.min(0.08, (time - lastTimestamp) / 1000 || 1 / 120));
      const instantVelocity = {
        x: (raw.x - lastRaw.x) / deltaSeconds,
        y: (raw.y - lastRaw.y) / deltaSeconds
      };
      const velocityBlend = 0.28 + resolved.velocityResponse * 0.42;
      velocity = {
        x: velocity.x + (instantVelocity.x - velocity.x) * velocityBlend,
        y: velocity.y + (instantVelocity.y - velocity.y) * velocityBlend
      };

      const speed = Math.hypot(velocity.x, velocity.y);
      // Keep meaningful stabilization at writing speed; the former full release made
      // Sharp, Balanced, and Smooth converge during the exact fast strokes we calibrate.
      const speedRelease = Math.min(0.55, (speed / 1800) * resolved.velocityResponse * 0.55);
      const effectiveSmoothing = resolved.smoothingStrength * (1 - speedRelease);
      const baseFollow = 1 - Math.min(0.88, effectiveSmoothing);
      const follow = 1 - Math.pow(1 - baseFollow, Math.max(0.25, deltaSeconds * 60));
      let output = {
        x: lastOutput.x + (raw.x - lastOutput.x) * follow,
        y: lastOutput.y + (raw.y - lastOutput.y) * follow
      };

      if (options.final) {
        output = raw;
      } else if (resolved.predictionMs > 0 && speed > 1) {
        const predictionSeconds = resolved.predictionMs / 1000;
        const predictedDx = velocity.x * predictionSeconds;
        const predictedDy = velocity.y * predictionSeconds;
        const predictedDistance = Math.hypot(predictedDx, predictedDy);
        const capRatio = predictedDistance > 12 ? 12 / predictedDistance : 1;
        output = {
          x: output.x + predictedDx * capRatio,
          y: output.y + predictedDy * capRatio
        };
      }

      const trackingLagPx = Math.hypot(raw.x - output.x, raw.y - output.y);
      lastRaw = raw;
      lastOutput = output;
      lastTimestamp = time;
      return { x: output.x, y: output.y, trackingLagPx };
    }
  };
}
